import { useCallback } from "react";
import { FACTOR_90, FACTOR_75 } from "../constants";

export const useVPPipelineCalcs = ({
  oportunidades,
  oportunidadesOtroPais,
  contratosData = [],
  contratosOtroPais = [],
  showInDollars,
  modoCombinado,
  tipoCambioActual,
  tipoCambioOtroPais,
  pais,
}) => {
  // Normaliza un contrato al formato de oportunidad para pipeline
  const normalizarContrato = (c, paisOrigen) => ({
    ...c,
    importe: c.importe_total,
    paisOrigen,
    esContrato: true,
  });

  // Helper: agrupa TODAS las ops (90% + 75% juntas, sin separar por estatus) por
  // client_id + país y devuelve la op de importe MÍNIMO de cada grupo. Cada cliente
  // cuenta una sola vez en el pipeline → escenario más conservador.
  const getMinOpsByClientAndPais = useCallback((ops) => {
    const byKey = {};
    ops.forEach((o) => {
      const key = `${o.client_id || o.cliente}-${o.paisOrigen}`;
      const importe = parseFloat(o.importe) || 0;
      if (!byKey[key] || importe < byKey[key].importe) {
        byKey[key] = { ...o, importe };
      }
    });
    return Object.values(byKey);
  }, []);

  // Helper: convierte el importe a la moneda de visualización (USD o modo combinado)
  const convertImporte = useCallback(
    (importe, paisOrigen) => {
      if (showInDollars || modoCombinado) {
        const rate = paisOrigen === pais ? tipoCambioActual : tipoCambioOtroPais || tipoCambioActual;
        return importe / rate;
      }
      return importe;
    },
    [showInDollars, modoCombinado, tipoCambioActual, tipoCambioOtroPais, pais]
  );

  // Helper: construye array de ops filtradas por un campo de mes dado
  const buildFilteredOps = useCallback(
    (monthIndex, campoMes) => {
      const filterByMonth = (ops) =>
        ops
          .filter((o) => o.estatus === "90%" || o.estatus === "75%")
          .filter((o) => !o.eliminada && o[campoMes])
          .filter((o) => {
            const [mesStr, anoStr] = o[campoMes].split("-");
            return Number(anoStr) === 2026 && Number(mesStr) === monthIndex + 1;
          });

      let allOps = filterByMonth(oportunidades).map((o) => ({ ...o, paisOrigen: pais }));

      // Agregar contratos normalizados
      const contratosNorm = contratosData
        .filter((c) => !c.eliminada && (c.estatus === "90%" || c.estatus === "75%") && c[campoMes])
        .filter((c) => {
          const [mesStr, anoStr] = c[campoMes].split("-");
          return Number(anoStr) === 2026 && Number(mesStr) === monthIndex + 1;
        })
        .map((c) => normalizarContrato(c, pais));
      allOps = [...allOps, ...contratosNorm];

      if (modoCombinado && oportunidadesOtroPais.length > 0) {
        const otroPais = pais === "Chile" ? "Perú" : "Chile";
        const opsOtro = filterByMonth(oportunidadesOtroPais).map((o) => ({ ...o, paisOrigen: otroPais }));
        allOps = [...allOps, ...opsOtro];

        // Contratos otro país
        if (contratosOtroPais.length > 0) {
          const contratosOtroNorm = contratosOtroPais
            .filter((c) => !c.eliminada && (c.estatus === "90%" || c.estatus === "75%") && c[campoMes])
            .filter((c) => {
              const [mesStr, anoStr] = c[campoMes].split("-");
              return Number(anoStr) === 2026 && Number(mesStr) === monthIndex + 1;
            })
            .map((c) => normalizarContrato(c, otroPais));
          allOps = [...allOps, ...contratosOtroNorm];
        }
      }

      return allOps;
    },
    [oportunidades, oportunidadesOtroPais, contratosData, contratosOtroPais, modoCombinado, pais]
  );

  // Pipeline ponderado por mes_cierre (para forecast de cierres). El factor se aplica
  // según el estatus de la op ganadora (la de importe mínimo) de cada cliente.
  const calcularPipeline = useCallback(
    (monthIndex) => {
      const allOps = buildFilteredOps(monthIndex, "mes_cierre");
      return getMinOpsByClientAndPais(allOps).reduce((sum, o) => {
        const factor = o.estatus === "90%" ? FACTOR_90 : FACTOR_75;
        return sum + convertImporte(o.importe, o.paisOrigen) * factor;
      }, 0);
    },
    [buildFilteredOps, getMinOpsByClientAndPais, convertImporte]
  );

  const calcularPipelineTotal = useCallback(() => {
    let total = 0;
    for (let i = 0; i < 12; i++) total += calcularPipeline(i);
    return total;
  }, [calcularPipeline]);

  // Pipeline BRUTO (sin factores) por mes_cierre — para columna de tabla
  const calcularPipelineBruto = useCallback(
    (monthIndex) => {
      const allOps = buildFilteredOps(monthIndex, "mes_cierre");
      return getMinOpsByClientAndPais(allOps).reduce((sum, o) => sum + convertImporte(o.importe, o.paisOrigen), 0);
    },
    [buildFilteredOps, getMinOpsByClientAndPais, convertImporte]
  );

  const calcularPipelineBrutoTotal = useCallback(() => {
    let total = 0;
    for (let i = 0; i < 12; i++) total += calcularPipelineBruto(i);
    return total;
  }, [calcularPipelineBruto]);

  // Pipeline ponderado por mes_factura (para forecast de facturación). Mismo criterio:
  // 1 op mínima por cliente, factor según el estatus de la op ganadora.
  const calcularPipelineFacturacion = useCallback(
    (monthIndex) => {
      const allOps = buildFilteredOps(monthIndex, "mes_factura");
      return getMinOpsByClientAndPais(allOps).reduce((sum, o) => {
        const factor = o.estatus === "90%" ? FACTOR_90 : FACTOR_75;
        return sum + convertImporte(o.importe, o.paisOrigen) * factor;
      }, 0);
    },
    [buildFilteredOps, getMinOpsByClientAndPais, convertImporte]
  );

  const getPipelineItems = useCallback(
    (monthIndex, isTotal = false) => {
      const filterByMonth = (ops, month) =>
        ops.filter((o) => {
          if (!o.mes_cierre) return false;
          const [mesStr, anoStr] = o.mes_cierre.split("-");
          if (isTotal) return Number(anoStr) === 2026;
          return Number(anoStr) === 2026 && Number(mesStr) === month + 1;
        });

      const ops90Local = filterByMonth(
        oportunidades.filter((o) => o.estatus === "90%" && !o.eliminada),
        monthIndex
      );
      const ops75Local = filterByMonth(
        oportunidades.filter((o) => o.estatus === "75%" && !o.eliminada),
        monthIndex
      );

      let allOps = [
        ...ops90Local.map((o) => ({ ...o, paisOrigen: pais })),
        ...ops75Local.map((o) => ({ ...o, paisOrigen: pais })),
      ];

      // Contratos locales
      const contratosLocales = filterByMonth(
        contratosData.filter((c) => (c.estatus === "90%" || c.estatus === "75%") && !c.eliminada),
        monthIndex
      ).map((c) => normalizarContrato(c, pais));
      allOps = [...allOps, ...contratosLocales];

      if (modoCombinado && oportunidadesOtroPais.length > 0) {
        const otroPais = pais === "Chile" ? "Perú" : "Chile";
        const ops90Otro = filterByMonth(
          oportunidadesOtroPais.filter((o) => o.estatus === "90%" && !o.eliminada),
          monthIndex
        );
        const ops75Otro = filterByMonth(
          oportunidadesOtroPais.filter((o) => o.estatus === "75%" && !o.eliminada),
          monthIndex
        );
        allOps = [
          ...allOps,
          ...ops90Otro.map((o) => ({ ...o, paisOrigen: otroPais })),
          ...ops75Otro.map((o) => ({ ...o, paisOrigen: otroPais })),
        ];

        // Contratos otro país
        if (contratosOtroPais.length > 0) {
          const contratosOtro = filterByMonth(
            contratosOtroPais.filter((c) => (c.estatus === "90%" || c.estatus === "75%") && !c.eliminada),
            monthIndex
          ).map((c) => normalizarContrato(c, otroPais));
          allOps = [...allOps, ...contratosOtro];
        }
      }

      // Deduplicación: 1 op por client_id + país → menor valor entre TODAS sus ops
      // (90% + 75% juntas, ops y contratos compiten por el mínimo)
      const byClientAndPais = {};
      allOps.forEach((o) => {
        const key = `${o.client_id || o.cliente}-${o.paisOrigen}`;
        const importe = parseFloat(o.importe) || 0;
        if (!byClientAndPais[key] || importe < byClientAndPais[key].importe) {
          byClientAndPais[key] = { ...o, importe };
        }
      });

      return Object.values(byClientAndPais);
    },
    [oportunidades, oportunidadesOtroPais, contratosData, contratosOtroPais, pais, modoCombinado]
  );

  return {
    calcularPipeline,
    calcularPipelineTotal,
    calcularPipelineBruto,
    calcularPipelineBrutoTotal,
    calcularPipelineFacturacion,
    getPipelineItems,
  };
};
